// src/components/Figma/Hero.tsx
import { useState } from "react";

import HeroImage from "@/assets/hero-image.svg";
import MessageIcon from "@/assets/message.svg";
import MessageIconHover from "@/assets/message-hover.svg";
import NotesIcon from "@/assets/notes.svg";
import NotesIconHover from "@/assets/notes-hover.svg";
import ArrowRight from "@/assets/arrow-right.svg";

const cardsData = [
  {
    key: "initiate",
    title: "Initiate a Session",
    desc: "For me and another person",
    icon: MessageIcon,
    iconHover: MessageIconHover,
    classNames: "border-[#FDE29A] hover:border-[#FBBC05]",
  },
  {
    key: "open",
    title: "Open My Session",
    desc: "Continue conversations",
    icon: NotesIcon,
    iconHover: NotesIconHover,
    classNames: "border-[#A7C7F8] hover:border-[#4285F4]",
  },
] as const;

export function Hero() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  // Investor preview: no navigation, just visual interaction
  const handleCardClick = () => {
    // intentionally no-op
  };

  return (
    <section className="flex w-full px-4 sm:px-6 md:px-10 py-8 sm:py-12 md:py-16 lg:py-[100px] items-center bg-[#DCE9FC]">
      <div className="grid grid-cols-1 lg:grid-cols-2 w-full max-w-[1440px] mx-auto gap-8 md:gap-12">
        <div className="flex flex-col items-start w-full max-w-[712px] gap-6 sm:gap-8 md:gap-10">
          <div className="flex flex-col items-start justify-start gap-2 sm:gap-3">
            <h1 className="text-[#2C2C2C] font-family-gabarito text-[32px] sm:text-[40px] md:text-[52px] lg:text-[64px] max-w-[620px] font-bold leading-[36px] sm:leading-[44px] md:leading-[56px] lg:leading-[68px]">
              Resolve. Repair. Reconnect.
            </h1>
            <p className="text-[#6B6B6B] font-family-gabarito text-lg sm:text-xl md:text-2xl lg:text-[28px] xl:text-[32px] leading-snug sm:leading-7 md:leading-8 lg:leading-9">
              Turn your conflicts into breakthroughs.

            </p>
          </div>

          {/* Cards (no routing) */}
          <div className="flex flex-col md:flex-row items-start justify-start gap-3 md:gap-4 w-full z-10">
            {cardsData.map((card, index) => (
              <div
                key={card.key}
                role="button"
                tabIndex={0}
                aria-label={card.title}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCardClick();
                  }
                }}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`flex w-full sm:w-auto max-w-[360px] shrink-0 cursor-pointer
                            px-4 sm:px-5 py-3 sm:py-3.5
                            flex-col items-start gap-1.5
                            rounded-[18px] border-l-4 bg-white
                            shadow-[0_8px_18px_0_rgba(0,0,0,0.06)]
                            transition-all duration-300 ease-in-out ${card.classNames}`}
              >
                <div className="flex items-center gap-2.5 justify-start">
                  <div className="relative w-[28px] h-[28px] sm:w-[32px] sm:h-[32px] shrink-0">
                    <img
                      src={card.icon}
                      alt=""
                      className={`absolute inset-0 transition-opacity duration-300 ease-in-out ${
                        hoveredCard === index ? "opacity-0" : "opacity-100"
                      }`}
                    />
                    <img
                      src={card.iconHover}
                      alt=""
                      className={`absolute inset-0 transition-opacity duration-300 ease-in-out ${
                        hoveredCard === index ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </div>
                  <h3
                    className="flex items-center gap-2 text-[#2C2C2C] font-family-gabarito
                               text-[17px] sm:text-[18px] md:text-[19px]
                               font-medium leading-[22px] sm:leading-[24px] md:leading-[26px] whitespace-nowrap"
                  >
                    {card.title}
                    <img
                      src={ArrowRight}
                      alt=""
                      className="inline shrink-0 w-4 h-4"
                    />
                  </h3>
                </div>
                <p
                  className="text-[#6B6B6B] font-family-reddit-sans
                             text-[13px] sm:text-[14px] md:text-[15px]
                             leading-[18px] sm:leading-[19px] md:leading-[20px]"
                >
                  {card.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center lg:items-end lg:justify-end">
          <img
            src={HeroImage}
            className="w-full max-w-[380px] sm:max-w-[460px] lg:max-w-none"
            alt="Hero"
          />
        </div>
      </div>
    </section>
  );
}
